<template>
  <ModalCreateEditCampaign
    v-show="modalInfoCampaign"
    :campaign="selectedCampaign"
    @close="closeInformationCampaignModal"
    @create="createCampaignTransaction"
    @edit="editCampaignTransaction"
    @remove="deleteAdditionalImageCampaignTransaction"
  />
  <ModalConfirmation
    :message="confirmMessage"
    @accept="closeConfirmModalAsync(deleteCampaignTransaction)"
    @deny="closeConfirmModalAsync"
    v-show="showConfirmModal"
  />
  <div class="wrapper-campaigns">
    <div class="above-info">
      <p>Campañas</p>
      <StandardButton
        :buttonLabel="'Crear campaña'"
        @click="createNewCampaign"
      />
    </div>

    <ul class="wrapper-campaigns" v-if="campaigns.length > 0">
      <li class="campaign" v-for="(campaign, index) in campaigns" :key="index">
        <div class="campaign-name">
          <p>{{ campaign.campaignName }}</p>
        </div>
        <EditButton @click="editCampaignSelected(campaign)" />
        <DeleteButton @click="selectDeletedCampaign(campaign)" />
        <div class="wrapper-icon-campaign">
          <img :src="campaign.icon" alt="" class="icon-campaign" />
        </div>

        <div class="wrapper-image-campaign image1">
          <img
            v-if="campaign.additional_image1"
            :src="campaign.additional_image1"
            alt=""
            class="image-campaign"
          />
          <p v-else class="empty">
            Sin imagen.<br />
            Necesaria para verse en la app móvil.
          </p>
        </div>
        <div class="wrapper-image-campaign image2">
          <img
            v-if="campaign.additional_image2"
            :src="campaign.additional_image2"
            alt=""
            class="image-campaign"
          />
          <p v-else class="empty">Sin imagen</p>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import ModalCreateEditCampaign from "./components/ModalCreateEditCampaign.vue";
import ModalConfirmation from "@/components/commons/modals/ModalConfirmation.vue";
import { campaignsUse } from "../campaigns/use/campaignsUse";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import EditButton from "@/components/commons/controls/EditButton.vue";
import DeleteButton from "@/components/commons/controls/DeleteButton.vue";
import { spinnerProcessKey, successProcessKey } from "@/main";
import { confirmCancelMessageManager } from "@/ux/utils/managers/confirmCancelMessageManager";
import { Campaign } from "@/business/campaign/domain/campaign";

export default defineComponent({
  name: "CampaignsView",
  components: {
    ModalCreateEditCampaign,
    ModalConfirmation,
    StandardButton,
    EditButton,
    DeleteButton,
  },

  setup() {
    const successProcess = inject(successProcessKey)!;
    const { showSuccessModal } = successProcess;

    const spinnerProcess = inject(spinnerProcessKey)!;
    const { offSpinnerState, onSpinnerState } = spinnerProcess;

    const {
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
    } = confirmCancelMessageManager();

    const {
      modalInfoCampaign,
      closeInformationCampaignModal,
      createCampaignOperation,
      editCampaignOperation,
      deleteCampaignOperation,
      selectedCampaign,
      createNewCampaign,
      editCampaignSelected,
      campaigns,
      deleteAdditionalImageCampaignOperation,
    } = campaignsUse();

    async function createCampaignTransaction(campaign: {
      name: string;
      icon: File;
      image1: File | null;
      image2: File | null;
    }) {
      try {
        await onSpinnerState();
        await createCampaignOperation(
          campaign.name,
          campaign.icon,
          campaign.image1,
          campaign.image2
        );
        await offSpinnerState();
        showSuccessModal(
          "La campaña fue creada correctamente",
          closeInformationCampaignModal
        );
      } catch (error) {
        await offSpinnerState();
        throw error;
      }
    }

    async function editCampaignTransaction(campaign: {
      name: string;
      icon: File;
      image1: File | null;
      image2: File | null;
    }) {
      try {
        await onSpinnerState();
        await editCampaignOperation(
          campaign.name,
          campaign.icon,
          campaign.image1,
          campaign.image2
        );
        await offSpinnerState();
        showSuccessModal(
          "La campaña fue actualizada correctamente",
          closeInformationCampaignModal
        );
      } catch (error) {
        await offSpinnerState();
        throw error;
      }
    }

    async function selectDeletedCampaign(campaign: Campaign) {
      selectedCampaign.value = campaign;
      openConfirmModal("¿Estás seguro de eliminar la campaña?");
    }

    async function deleteAdditionalImageCampaignTransaction(position: number) {
      try {
        await onSpinnerState();
        await deleteAdditionalImageCampaignOperation(position);
        await offSpinnerState();
        showSuccessModal("La campaña fue actualizada correctamente");
      } catch (error) {
        await offSpinnerState();
        throw error;
      }
    }

    async function deleteCampaignTransaction(): Promise<void> {
      try {
        await onSpinnerState();
        await deleteCampaignOperation();
        await offSpinnerState();
        showSuccessModal("La campaña ha sido eliminada correctamente.");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    return {
      modalInfoCampaign,
      closeInformationCampaignModal,
      createCampaignTransaction,
      editCampaignTransaction,
      selectDeletedCampaign,
      deleteCampaignTransaction,
      showConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
      createNewCampaign,
      editCampaignSelected,
      campaigns,
      selectedCampaign,
      deleteAdditionalImageCampaignTransaction,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/campaigns/styles/campaignsStyle";
</style>
